<!--
  The final confirmation of an agent connection: the agent's fingerprint
  and harness, the subject it would read (a pull request, a compare or a
  branch) and what it may read of it, then Connect or Decline. An agent
  already connected is named, with what it can read of the subject now and
  what Connect would add to that. A branch's history says plainly how far
  it reaches: most of the repository, the default branch's above all.
  Shown for a pairing link and for an already paired agent's
  grant request alike (state/relayAgents.svelte.ts). Every string from the
  agent is shown as text.

  An agent already connected is shown with when it was connected, when it
  was last heard from, and what else it can read here.

  Connect is armed only once the card has been shown, unmoved, for ARM_MS
  (`relayAgents.armDelay`): a card that appears or moves under the
  pointer (an agent timing its request, a card above it going away) can't
  be approved by a click aimed at something else. It re-arms whenever the
  card moves or the list around it changes (`order`). The wait is shorter
  than a person reads the card, so it is said only to assistive
  technology, and the button only looks disabled meanwhile.
-->
<script lang="ts">
  import { kindOf } from '../api/relay-grants';
  import { sameSubject } from '../api/relaySubject';
  import { harnessText } from '../logic/relayHarness';
  import { kindName, kindTitle, lastSeenText, scopeText, subjectHref } from '../logic/relayText';
  import { ARM_MS, relayAgents, type Approval } from '../state/relayAgents.svelte';
  import RelayScope from './RelayScope.svelte';
  interface Props {
    approval: Approval;
    linked?: boolean;
    /** The approvals' keys in their order: a change re-arms Connect. */
    order: string;
    /** Called as the card goes away while focus is in it, so the list can put focus somewhere. */
    onleave?: () => void;
  }
  let { approval, linked = false, order, onleave }: Props = $props();
  /** Whether this browser's pairings and grants have been read, so the preset can be trusted. */
  const ready = $derived(relayAgents.pairingsRead);
  /** What the boxes start with, as the grants are now (a grant narrowed elsewhere meanwhile included). */
  const preset = $derived(relayAgents.presetOf(approval));
  const presetKey = $derived(preset.join());
  // svelte-ignore state_referenced_locally
  let scope = $state([...preset]);
  // The boxes follow the preset until the person changes one.
  let touched = false;
  $effect(() => { const p = preset; if (!touched) scope = [...p]; });
  let busy = $state(false);
  let error = $state('');
  /** A decline failed: the card can still be dropped here. */
  let stuck = $state(false);
  let armed = $state(false);
  let card: HTMLElement | undefined = $state();
  let focused = false;
  const uid = $props.id();
  // As this browser has them now, which may change while the card is shown.
  const known = $derived(relayAgents.connection(approval.connection));
  const name = $derived(known?.name ?? approval.name);
  const current = $derived(known && relayAgents.current(approval.connection, approval.subject));
  /** The other subjects an agent already connected can read, by key. */
  const elsewhere = $derived(known ? relayAgents.grants.filter((g) => g.connection === known.id && g.status === 'granted' && !sameSubject(g.subject, approval.subject)).map((g) => g.subject) : []);
  const kind = $derived(kindOf(approval.subject));
  /** `pull request`, `compare` or `branch`. */
  const noun = $derived(kindName(approval.subject));
  const href = $derived(subjectHref(approval.subject));
  /** When an agent already connected was paired, as a date. */
  const paired = $derived(known?.paired ? new Date(known.paired).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
  /** What Connect would let an agent already reading the subject read besides. */
  const adds = $derived(current ? scope.filter((c) => !current.scope.includes(c)) : []);
  const why = (e: unknown) => (e instanceof Error ? e.message : String(e));
  const where = () => { const r = card?.getBoundingClientRect(); return r ? `${r.left},${r.top}` : ''; };
  /** Where the card was when Connect was armed. */
  let armedAt = '';
  let arming: AbortController | undefined;
  function arm() {
    arming?.abort();
    armed = false;
    const ctl = (arming = new AbortController());
    const at = where();
    void relayAgents.armDelay(ARM_MS, ctl.signal).then(() => {
      if (ctl.signal.aborted) return;
      // Moved while waiting: wait again from here.
      if (where() !== at) return arm();
      armedAt = at;
      armed = true;
    });
  }
  // Re-armed when the list around it changes, or this approval does in place
  // (an agent asking again with a request code), or its boxes are changed
  // under the pointer (the pairings read, a grant changed elsewhere).
  $effect(() => {
    void order;
    void approval;
    void presetKey;
    void ready;
    if (!card) return;
    arm();
    return () => arming?.abort();
  });
  $effect(() => () => { if (focused) onleave?.(); });
  // While busy the buttons and boxes are aria-disabled, not disabled: one
  // that is focused keeps focus (a disabled one would drop it to the page).
  async function act(fn: () => Promise<void>, decline = false) {
    if (busy) return;
    busy = true; error = '';
    try { await fn(); } catch (e) { error = why(e); if (decline) stuck = true; } finally { busy = false; }
  }
  function connect() {
    // Not armed yet, or moved since: this click wasn't aimed at it.
    if (!armed || busy || !ready) return;
    if (where() !== armedAt) return arm();
    void act(() => relayAgents.approve(approval, scope));
  }
</script>

<section
  class="relay-approval"
  bind:this={card}
  aria-labelledby="{uid}-h"
  aria-describedby="{uid}-what"
  aria-busy={busy ? 'true' : undefined}
  onfocusin={() => (focused = true)}
  onfocusout={(e) => { if (!card?.contains(e.relatedTarget as Node | null)) focused = false; }}
>
  <h3 id="{uid}-h" tabindex="-1">{approval.kind === 'grant' ? `Let ${name ?? 'your agent'} read this ${noun}?` : known ? `Connect ${known.name} again?` : 'Connect the agent that sent this link?'}</h3>
  <dl>
    <dt>Agent fingerprint</dt><dd class="fingerprint">{approval.fingerprint}</dd>
    {#if name}<dt>Agent</dt><dd class="name">{name}</dd>{/if}
    <dt>Says it is</dt><dd class="agent">{harnessText(approval.harness)}</dd>
    <dt>{kindTitle(kind)}</dt>
    <dd class="subject">{#if linked && href}<a {href}>{approval.subject}</a>{:else}{approval.subject}{/if}</dd>
    {#if known}
      {#if paired}<dt>Connected</dt><dd class="paired">{paired}</dd>{/if}
      <dt>Last seen</dt><dd class="seen">{lastSeenText(relayAgents.lastSeen(known.id), relayAgents.now).replace(/^Last seen /, '').replace(/^Not heard from yet$/, 'not yet')}</dd>
      <dt>Also reads</dt><dd class="elsewhere">{elsewhere.length ? elsewhere.join(', ') : 'nothing else'}</dd>
    {/if}
  </dl>
  {#if known && current}<p class="already">{known.name} is already connected and can read {scopeText(current.scope)} of this {noun}. Connect keeps that unless you check more.</p>
  {:else if known}<p class="already">{known.name} is already connected to this browser, but can’t read this {noun} yet.</p>{/if}
  <p id="{uid}-what" class="muted">The agent says what it is; only the fingerprint identifies it. Check that the fingerprint matches the one your agent reported. If it doesn’t, or you didn’t ask an agent to connect, decline: the agent would read what you check below.</p>
  {#if kind === 'ref'}
    <p class="reach" id="{uid}-reach">
      A branch’s history reaches far: what this agent may read of it covers every file at every commit it names on the branch, so a long branch, and
      the default branch above all, shows it most of the repository. History (the commits and force-pushes) is unchecked unless you check it.
    </p>
  {/if}
  {#if ready}
    <RelayScope legend="It may read" {kind} {scope} disabled={busy} onchange={(s) => { touched = true; scope = s; }} />
  {:else if relayAgents.loaded && relayAgents.error}
    <p class="setting-problem" role="alert">This browser’s agents couldn’t be read, so this link can’t be approved yet: {relayAgents.error}</p>
    <div><button class="btn small" onclick={() => void relayAgents.refresh()}>Try again</button></div>
  {:else}
    <p class="muted" role="status">Reading this browser’s agents…</p>
  {/if}
  {#if adds.length}<p class="adds" role="status">Connect would also let it read: {scopeText(adds)}.</p>{/if}
  <div class="actions">
    <button
      class="btn small primary"
      class:arming={!armed}
      aria-disabled={armed && !busy && ready ? undefined : 'true'}
      aria-describedby={armed ? undefined : `${uid}-arming`}
      onclick={connect}>Connect</button
    >
    <button class="btn small" aria-disabled={busy ? 'true' : undefined} onclick={() => act(() => relayAgents.decline(approval), true)}>Decline</button>
    {#if stuck}<button class="btn small" aria-disabled={busy ? 'true' : undefined} onclick={() => { if (!busy) relayAgents.dismiss(approval); }}>Dismiss here</button>{/if}
    {#if !armed}<span class="sr-only" id="{uid}-arming">Connect is available in a moment.</span>{/if}
  </div>
  {#if error}<p class="setting-problem" role="alert">{error}{#if stuck} You can dismiss it here; the request expires on your computer.{/if}</p>{/if}
</section>

<style>
  .relay-approval { display: grid; gap: 10px; padding: 14px 16px; border: 1px solid var(--accent); border-radius: 10px; background: var(--surface); font-size: 13px; box-shadow: 0 10px 30px #0003; }
  h3 { font-size: 14px; }
  h3:focus { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 4px; }
  dl { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 12px; margin: 0; }
  dt { color: var(--muted); }
  dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
  .fingerprint { font-family: ui-monospace, SFMono-Regular, monospace; font-weight: 650; letter-spacing: .04em; }
  .reach { margin: 0; padding: 8px 10px; border-radius: 8px; background: var(--warnbg); color: var(--fg); }
  .actions { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .btn.arming, .btn[aria-disabled='true'] { opacity: .45; cursor: default; }
  p { margin: 0; }
  .adds { padding: 6px 8px; border-radius: 6px; background: var(--warnbg); color: var(--fg); }
</style>
